import * as microsoftTeams from "@microsoft/teams-js";
import { generateCodeChallenge, generateCodeVerifier } from "./pkceUtils";
import { jwtDecode } from "jwt-decode";
import {
  normalizeAppRole,
  clearAuthorizationCapabilities,
} from "../constants/accessRights";

interface UserInfo {
  userName?: string;
  userPrincipalName?: string;
  userObjectId?: string;
  tid?: string;
  lang?: string;
}

interface TokenClaims {
  roles?: string[];
  role?: string;
  exp?: number;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  upn?: string;
  email?: string;
}

function parseScopeList(rawScopes: string): string {
  return Array.from(new Set(rawScopes.split(/\s+/).filter(Boolean))).join(" ");
}

// The OAuth redirect_uri MUST match the origin the app is currently running on.
// PKCE stores the code_verifier in this origin's sessionStorage; if Microsoft
// redirects back to a different origin (e.g. a hardcoded prod VITE_CALLBACK_URL
// while developing on localhost), the verifier is unreachable and the token
// exchange fails with "No code verifier found". So we honour VITE_CALLBACK_URL
// only when its origin matches the current one, otherwise fall back to the
// runtime origin. Each origin must still be registered in the Azure app.
function resolveRedirectUri(): string {
  const runtimeRedirectUri = `${window.location.origin}/auth/callback`;
  const configured = import.meta.env.VITE_CALLBACK_URL?.trim();
  if (!configured) return runtimeRedirectUri;

  try {
    if (new URL(configured).origin === window.location.origin) {
      return configured;
    }
    console.warn(
      `⚠️ VITE_CALLBACK_URL origin (${new URL(configured).origin}) does not match current origin (${window.location.origin}); using ${runtimeRedirectUri}`,
    );
  } catch {
    console.warn(
      `⚠️ VITE_CALLBACK_URL is not a valid URL ("${configured}"); using ${runtimeRedirectUri}`,
    );
  }
  return runtimeRedirectUri;
}

class TeamsService {
  private static instance: TeamsService;
  private initialized: boolean = false;
  private userInfo: UserInfo | null = null;
  private isRunningInTeams: boolean = false;

  private constructor() {}

  private cacheUserRoleFromToken(token: string): void {
    try {
      const claims = jwtDecode<TokenClaims>(token);
      const rawRole = claims.roles?.[0] ?? claims.role ?? null;
      const appRole = normalizeAppRole(rawRole);
      sessionStorage.setItem("user_role", appRole);

      // Derive a display name from the token claims so the UI shows the actual
      // signed-in user (falling back through the most useful claims).
      const fullName = [claims.given_name, claims.family_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      const displayName =
        claims.name ||
        fullName ||
        claims.preferred_username ||
        claims.upn ||
        claims.email ||
        "";
      if (displayName) {
        sessionStorage.setItem("user_name", displayName);
      }

      console.log("👤 User role mapped from token:", appRole);
    } catch (error) {
      console.warn("⚠️ Failed to decode role claims from token", error);
      sessionStorage.setItem("user_role", "Viewer");
    }
  }

  // Returns true if the access token is missing an exp, already expired, or within
  // a 2-minute renewal window. Treated as expired so we refresh proactively instead
  // of letting the API reject a stale token with a 401 (which bounces to login).
  private isTokenExpired(token: string): boolean {
    try {
      const { exp } = jwtDecode<TokenClaims>(token);
      if (!exp) return true;
      return Date.now() >= exp * 1000 - 120_000;
    } catch {
      return true;
    }
  }

  private clearStoredTokens(): void {
    sessionStorage.removeItem("teams_token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("user_role");
    sessionStorage.removeItem("user_name");
  }

  // Silently exchanges the stored refresh token for a fresh access token. Returns the
  // new access token on success, or null if no refresh token exists or the refresh fails
  // (e.g. the refresh token itself expired), in which case the caller must re-authenticate.
  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = sessionStorage.getItem("refresh_token");
    if (!refreshToken) {
      console.log("🔍 No refresh token available for silent renewal");
      return null;
    }

    const tenantId = import.meta.env.VITE_TENANT_ID;
    const clientId = import.meta.env.VITE_CLIENT_ID;
    const configuredScopes =
      import.meta.env.VITE_SCOPE?.trim() ||
      import.meta.env.VITE_OIDC_SCOPES?.trim() ||
      "openid profile email User.Read";
    const scope = parseScopeList(configuredScopes);
    const tokenEndpoint = import.meta.env.VITE_ACCESS_TOKEN_URL.replace(
      "{tenant-id}",
      tenantId,
    );

    try {
      console.log("🔄 Refreshing access token via refresh_token grant");
      const response = await fetch(tokenEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          client_id: clientId,
          scope,
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        }),
      });

      const data = await response.json();
      if (data.access_token) {
        sessionStorage.setItem("teams_token", data.access_token);
        // Azure rotates refresh tokens; persist the new one when present.
        if (data.refresh_token) {
          sessionStorage.setItem("refresh_token", data.refresh_token);
        }
        this.cacheUserRoleFromToken(data.access_token);
        console.log("✅ Access token refreshed silently");
        return data.access_token;
      }

      console.warn("⚠️ Refresh token grant returned no access token:", data.error);
      return null;
    } catch (error) {
      console.error("❌ Refresh token grant failed:", error);
      return null;
    }
  }

  public static getInstance(): TeamsService {
    if (!TeamsService.instance) {
      TeamsService.instance = new TeamsService();
    }
    return TeamsService.instance;
  }

  public async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      await microsoftTeams.app.initialize();
      this.isRunningInTeams = true;
      this.initialized = true;
      console.log("Teams SDK initialized successfully");
    } catch (error) {
      console.log("Not running in Teams, will use standalone auth");
      this.isRunningInTeams = false;
      this.initialized = true;
    }
  }

  private async getStandaloneToken(loginHint?: string): Promise<string> {
    console.log("🔄 getStandaloneToken() called");

    const tenantId = import.meta.env.VITE_TENANT_ID;
    const clientId = import.meta.env.VITE_CLIENT_ID;
    const configuredScopes =
      import.meta.env.VITE_SCOPE?.trim() ||
      import.meta.env.VITE_OIDC_SCOPES?.trim() ||
      "openid profile email User.Read";
    const scope = parseScopeList(configuredScopes);
    const redirectUri = resolveRedirectUri();

    console.log("🔧 Environment variables:", { tenantId, clientId, scope, redirectUri });

    // Check if we're on the callback URL with an auth code
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");

    console.log("🔍 URL params - code:", !!code, "error:", error);

    if (error) {
      console.error("❌ OAuth error from Microsoft:", error);
      throw new Error(`OAuth error: ${error}`);
    }

    if (code) {
      console.log("✅ Authorization code received, starting token exchange");

      // Get stored code verifier
      const codeVerifier = sessionStorage.getItem("code_verifier");
      if (!codeVerifier) {
        console.error("❌ No code verifier found in sessionStorage");
        throw new Error("No code verifier found");
      }

      console.log("🔑 Code verifier retrieved from sessionStorage");

      // Exchange code for token using PKCE
      const tokenEndpoint = import.meta.env.VITE_ACCESS_TOKEN_URL.replace(
        "{tenant-id}",
        tenantId,
      );

      console.log("🌐 Token endpoint:", tokenEndpoint);

      const requestBody = new URLSearchParams({
        client_id: clientId,
        scope: scope,
        code: code,
        redirect_uri: redirectUri,
        grant_type: "authorization_code",
        code_verifier: codeVerifier,
      });

      console.log("📤 Token exchange request body prepared");

      try {
        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: requestBody,
        });

        console.log("📥 Token exchange response status:", response.status);

        const data = await response.json();
        console.log("📄 Token exchange response data:", {
          has_access_token: !!data.access_token,
          has_error: !!data.error,
          error_description: data.error_description
        });

        if (data.access_token) {
          console.log("✅ Token received successfully");

          // Clean up PKCE values
          sessionStorage.removeItem("code_verifier");
          console.log("🧹 Cleaned up code verifier from sessionStorage");

          // Store token
          sessionStorage.setItem("teams_token", data.access_token);
          // Persist the refresh token so the access token can be renewed silently
          // when it expires, instead of forcing the user back to the login page.
          if (data.refresh_token) {
            sessionStorage.setItem("refresh_token", data.refresh_token);
            console.log("💾 Refresh token stored for silent renewal");
          } else {
            console.warn(
              "⚠️ No refresh token returned — ensure 'offline_access' is in the requested scope",
            );
          }
          this.cacheUserRoleFromToken(data.access_token);
          console.log("💾 Token stored in sessionStorage");

          // Always redirect to workspace after successful token exchange
          sessionStorage.removeItem("auth_return_url");
          console.log("🔗 Redirecting to /workspace");
          window.location.replace("/workspace");
          return data.access_token;
        } else {
          console.error("❌ No access token in response:", data);
          throw new Error(`Token exchange failed: ${data.error_description || 'Unknown error'}`);
        }
      } catch (fetchError) {
        console.error("❌ Token exchange fetch error:", fetchError);
        throw fetchError;
      }
    } else {
      console.log("🔄 No authorization code found, starting OAuth flow");

      // Store current URL before redirecting.
      // If we are starting auth from the login page, always return to /workspace.
      const currentPath = window.location.pathname;
      const returnUrl = currentPath === "/" || currentPath === "/login" ? "/workspace" : `${currentPath}${window.location.search}`;
      sessionStorage.setItem("auth_return_url", returnUrl);
      console.log("💾 Stored return URL:", returnUrl);

      // Generate PKCE values
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store code verifier for later use
      sessionStorage.setItem("code_verifier", codeVerifier);
      console.log("🔐 Generated and stored PKCE values");

      // Start auth flow with PKCE
      const authUrl = import.meta.env.VITE_AUTH_URL.replace(
        "{tenant-id}",
        tenantId,
      );
      const authEndpoint = new URL(authUrl);

      const params = new URLSearchParams({
        client_id: clientId,
        response_type: "code",
        redirect_uri: redirectUri,
        scope: scope,
        response_mode: "query",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
      });

      // Pre-fill the account on the Microsoft sign-in page when the user typed
      // their email on our login form (Option 1: redirect-based credential entry).
      if (loginHint) {
        params.set("login_hint", loginHint);
      }

      const finalUrl = `${authEndpoint}?${params}`;
      console.log("🚀 Redirecting to Microsoft OAuth:", finalUrl);
      console.log("🔎 Auth request redirect_uri:", redirectUri);

      window.location.href = finalUrl;
      return ""; // This line won't be reached due to redirect
    }
  }

  public async authenticate(loginHint?: string): Promise<string> {
    console.log("🔐 authenticate() called");

    if (!this.initialized) {
      console.error("❌ Service not initialized");
      throw new Error("Service not initialized");
    }

    // Check for cached token first
    const cachedToken = sessionStorage.getItem("teams_token");
    if (cachedToken && !this.isTokenExpired(cachedToken)) {
      if (!sessionStorage.getItem("user_role")) {
        this.cacheUserRoleFromToken(cachedToken);
      }
      console.log("✅ Found valid cached token, returning it");
      return cachedToken;
    }

    // Cached token is missing or expired. Attempt a silent refresh before forcing
    // the user through an interactive sign-in. This is the key fix for users being
    // repeatedly bounced to the login page once their access token expires.
    if (cachedToken) {
      console.log("⏳ Cached token expired, attempting silent refresh");
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        return refreshed;
      }
      // Refresh failed (no/expired refresh token); drop the dead token so a stale
      // value doesn't keep producing 401s and trapping the user on the login page.
      console.log("🧹 Silent refresh failed, clearing stale tokens");
      this.clearStoredTokens();
    }

    console.log("🔍 No valid token found");

    if (this.isRunningInTeams) {
      console.log("👥 Running in Teams, using Teams SDK authentication");

      try {
        const teamsResource = import.meta.env.VITE_TEAMS_RESOURCE?.trim();
        const token = teamsResource
          ? await microsoftTeams.authentication.getAuthToken({
              resources: [teamsResource],
              silent: true,
            })
          : await microsoftTeams.authentication.getAuthToken({
              silent: true,
            });
        sessionStorage.setItem("teams_token", token);
        this.cacheUserRoleFromToken(token);
        console.log("✅ Silent Teams token acquired");
        return token;
      } catch (error) {
        console.warn("⚠️ Silent token failed, trying interactive:", error);
        // If silent token acquisition fails, try interactive
        const token = await microsoftTeams.authentication.authenticate({
          url: window.location.origin + "/auth-start.html",
          width: 600,
          height: 535,
        });
        sessionStorage.setItem("teams_token", token);
        this.cacheUserRoleFromToken(token);
        console.log("✅ Interactive Teams token acquired");
        return token;
      }
    } else {
      console.log("🌐 Not running in Teams, using standalone OAuth");
      return this.getStandaloneToken(loginHint);
    }
  }

  // Clears all client-side authentication state and returns the user to the login
  // screen. Note: this signs the user out of the app only; the Microsoft SSO session
  // may persist, so a subsequent "Sign In with Microsoft" can complete without a
  // password prompt.
  public signOut(): void {
    console.log("🚪 Signing out");
    this.clearStoredTokens();
    clearAuthorizationCapabilities();
    sessionStorage.removeItem("code_verifier");
    sessionStorage.removeItem("auth_return_url");
    this.userInfo = null;
    window.location.replace("/");
  }

  public isRunningInTeamsApp(): boolean {
    return this.isRunningInTeams;
  }

  public async openShiftsApp(): Promise<void> {
    const appId = import.meta.env.VITE_SHIFTS_APP_ID;
    const tenantId = import.meta.env.VITE_TENANT_ID;
    const deeplinkUrl = `https://teams.microsoft.com/l/app/${appId}?tenantId=${tenantId}`;
    if (this.isRunningInTeamsApp()) {
      console.log("Opening shifts app in Teams", deeplinkUrl);
      microsoftTeams.app.openLink(deeplinkUrl);
    } else {
      console.log("Opening shifts app in browser", deeplinkUrl);
      window.open(deeplinkUrl, "_blank");
    }
  }

  public async getUserInfo(): Promise<UserInfo> {
    if (!this.initialized) {
      throw new Error("Teams SDK not initialized");
    }

    if (this.userInfo) {
      return this.userInfo;
    }

    try {
      const context = await microsoftTeams.app.getContext();
      this.userInfo = {
        userName: context.user?.userPrincipalName,
        userPrincipalName: context.user?.userPrincipalName,
        userObjectId: context.user?.id,
        tid: context.user?.tenant?.id,
        lang: context.app.locale,
      };
      return this.userInfo;
    } catch (error) {
      console.error("Failed to get user info:", error);
      throw error;
    }
  }

  public async getContext(): Promise<microsoftTeams.app.Context> {
    if (!this.initialized) {
      throw new Error("Teams SDK not initialized");
    }

    try {
      return await microsoftTeams.app.getContext();
    } catch (error) {
      console.error("Failed to get context:", error);
      throw error;
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public async getUserObjectID(): Promise<string> {
    const token = await this.authenticate();
    const decodedToken = jwtDecode(token);
    return decodedToken["oid"];
  }

  public async getUserName(): Promise<string> {
    const token = await this.authenticate();
    const decodedToken = jwtDecode(token);
    return `${decodedToken["given_name"]} ${decodedToken["family_name"]}`;
  }
}

export default TeamsService;
